import { ReactNode } from 'react';
import { useMediaQuery } from '@mui/material';
import { theme } from 'assets/themes/zizzi';
import { useStore } from 'providers';
import { ROUTES } from 'constants/routes';
import { SurprisePerkType } from 'models';
import { useHistory } from 'react-router-dom';
import { pushAnalyticsEvent } from 'helpers/analytics';
import TertiaryButton from 'components/TertiaryButton';
import Confetti from 'react-confetti';
import {
  StyledModal,
  ImageWrapper,
  Title,
  Wrapper,
  Header,
  Footer,
  FooterText,
  Achievement,
  StyledGarlicBread,
  StyledChocolateDessert,
  StyledPrimavera,
  StyledPasta,
  StyledDrinks,
  StyledFeast,
  Tick,
  Text,
  StyledButton,
} from './SurpriseAlertModal.styles';

interface SurprisePerkModalContent {
  achievementLine1: ReactNode;
  achievementLine2: string;
  title: string;
  text: string;
  callToAction: string;
}

export interface SurpriseModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: SurprisePerkType;
}

const SurpriseAlertModal = ({ isOpen, onClose, type }: SurpriseModalProps) => {
  const { store } = useStore();
  const { userData } = store || {};

  const firstName = userData?.personalDetails.firstName;

  const history = useHistory();
  const useFullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const confettiSettings = {
    colors: [theme.palette.orange],
    width: window.innerWidth,
  };

  if (isOpen) {
    pushAnalyticsEvent({ event: 'surprisePerkModalOpened' });
  }

  const handleOnClick = () => {
    pushAnalyticsEvent({ event: 'surprisePerkClaimPerkNow' });
    history.push({
      pathname: ROUTES.SURPRISE_PERK,
      state: { type },
    });
    onClose();
  };

  const handleOnClose = () => {
    pushAnalyticsEvent({ event: 'surprisePerkClaimPerkLater' });
    onClose();
  };

  const content: Record<SurprisePerkType, SurprisePerkModalContent> = {
    surprise: {
      achievementLine1: (
        <>
          4<sup>th</sup>
        </>
      ),
      achievementLine2: 'VISIT',
      title: 'Collect your Surprise Perk now!',
      text: `You're a real Zillionaire. You've unlocked your Surprise Perk for visiting Zizzi four times within one year!`,
      callToAction: 'Collect now',
    },
    'spin-to-win': {
      achievementLine1: 'Sign up',
      achievementLine2: 'COMPLETE',
      title: 'Collect your Welcome Perk now!',
      text: `Welcome to the club ${firstName}! To get your first taste of the Zillionaire life, spin the wheel and you'll receive a tasty treat.`,
      callToAction: 'Get My Welcome Perk',
    },
  };

  const { achievementLine1, achievementLine2, title, text, callToAction } = content[type];

  const isSurprisePerk = type === 'surprise';

  return (
    <StyledModal
      open={isOpen}
      onClose={handleOnClose}
      closeType="close"
      fullScreen={useFullScreen}
      aria-label="Surprise Modal"
    >
      <Wrapper>
        <Header>
          <Achievement>
            <Tick />
            <Text variant="h3">{achievementLine1}</Text>
            <Text variant="h3">{achievementLine2}</Text>
          </Achievement>

          <Title variant="h1">{title}</Title>

          <ImageWrapper>
            <StyledGarlicBread />
            <StyledChocolateDessert />
            <StyledPrimavera />
            <StyledPasta />
            <StyledDrinks />
            <StyledFeast />
          </ImageWrapper>
        </Header>

        <Footer>
          <FooterText variant="body1">{text}</FooterText>

          <StyledButton
            fullWidth
            onClick={handleOnClick}
            variant="contained"
            color="primary"
            disableRipple
          >
            {callToAction}
          </StyledButton>

          {isSurprisePerk && (
            <TertiaryButton fullWidth onClick={handleOnClose}>
              Collect later
            </TertiaryButton>
          )}
        </Footer>
      </Wrapper>

      <Confetti {...confettiSettings} />
    </StyledModal>
  );
};

export default SurpriseAlertModal;
